/**
 * "Still there?" — the last minute of an idle session.
 *
 * Deliberately not a scare. The countdown ring carries the urgency, so
 * the words do not have to: a serif line, one sentence, and the two
 * things you can do about it.
 *
 * Escape and a backdrop click do NOT dismiss this. Every other dialog in
 * the app closes that way, and here that instinct would silently mean
 * "stay signed in" — the opposite of what someone walking away from a
 * laptop wants, and indistinguishable from a cat on the keyboard. The
 * only ways out are the two buttons.
 */

import { Box, Dialog, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';

import { colors, docLeading, uiType } from '../theme';

const GOLD = colors.goldDark;
const MotionBox = motion.create(Box);

const RING = 92;
const STROKE = 3;
const RADIUS = (RING - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/** Art-deco corner bracket, matching the announcement modal. */
function CornerBracket({ top, left }: { top: boolean; left: boolean }) {
  const inset = 14;
  const size = 15;
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        ...(top ? { top: inset } : { bottom: inset }),
        ...(left ? { left: inset } : { right: inset }),
        width: size,
        height: size,
        pointerEvents: 'none',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          bgcolor: GOLD,
          opacity: 0.45,
        },
        '&::before': {
          ...(top ? { top: 0 } : { bottom: 0 }),
          ...(left ? { left: 0 } : { right: 0 }),
          width: '100%',
          height: '1px',
        },
        '&::after': {
          ...(top ? { top: 0 } : { bottom: 0 }),
          ...(left ? { left: 0 } : { right: 0 }),
          width: '1px',
          height: '100%',
        },
      }}
    />
  );
}

export function IdleWarningDialog({
  open,
  secondsLeft,
  totalSeconds,
  onStay,
  onSignOut,
}: {
  open: boolean;
  secondsLeft: number;
  totalSeconds: number;
  onStay: () => void;
  onSignOut: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const progress = Math.max(0, Math.min(1, secondsLeft / totalSeconds));
  // Under ten seconds the ring turns; the number alone is easy to miss
  // at the edge of vision.
  const urgent = secondsLeft <= 10;
  const ringColor = urgent ? colors.error : GOLD;

  return (
    <Dialog
      open={open}
      // No onClose: Escape and backdrop clicks must not answer for them.
      maxWidth="xs"
      fullWidth
      transitionDuration={0}
      aria-labelledby="idle-title"
      aria-describedby="idle-body"
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          bgcolor: colors.brandCream,
          border: `1px solid ${GOLD}3d`,
          boxShadow: '0 30px 70px -16px rgba(15, 10, 7, 0.45)',
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15, 10, 7, 0.68)' } } }}
    >
      <MotionBox
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.95, y: 12 }}
        animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
        transition={
          reduceMotion ? { duration: 0.2 } : { type: 'spring', stiffness: 250, damping: 25 }
        }
        sx={{ position: 'relative', px: { xs: 3.5, sm: 5 }, pt: 4.25, pb: 3.5 }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(to right, transparent, ${ringColor}, transparent)`,
            transition: 'background 300ms ease',
          }}
        />

        {/* ---- Countdown ring ----
             The seconds sit inside a ring that empties as they run out,
             so the state is legible at a glance from across a room —
             which is exactly where the person this is aimed at is. */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ position: 'relative', width: RING, height: RING }}>
            <Box
              component="svg"
              aria-hidden
              viewBox={`0 0 ${RING} ${RING}`}
              sx={{ width: RING, height: RING, transform: 'rotate(-90deg)' }}
            >
              <circle
                cx={RING / 2}
                cy={RING / 2}
                r={RADIUS}
                fill="none"
                stroke={`${GOLD}26`}
                strokeWidth={STROKE}
              />
              <circle
                cx={RING / 2}
                cy={RING / 2}
                r={RADIUS}
                fill="none"
                stroke={ringColor}
                strokeWidth={STROKE}
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
                style={{ transition: 'stroke-dashoffset 1s linear, stroke 300ms ease' }}
              />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontSize: uiType.display,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: urgent ? colors.error : colors.textPrimary,
                  transition: 'color 300ms ease',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {secondsLeft}
              </Typography>
              <Typography
                sx={{
                  fontSize: uiType.micro,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                  mt: 0.25,
                }}
              >
                {secondsLeft === 1 ? 'second' : 'seconds'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography
          id="idle-title"
          sx={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: { xs: uiType.headline, sm: 28 },
            fontWeight: 600,
            color: colors.textPrimary,
            textAlign: 'center',
            lineHeight: docLeading.tight,
            letterSpacing: '-0.01em',
            mt: 2.5,
          }}
        >
          Still there?
        </Typography>

        <Typography
          id="idle-body"
          sx={{
            fontSize: uiType.body,
            lineHeight: docLeading.relaxed,
            color: colors.textSecondary,
            textAlign: 'center',
            mt: 1.25,
            mx: 'auto',
            maxWidth: 300,
          }}
        >
          You have been idle for a while, so we are about to sign you out and leave this device
          clean.
        </Typography>

        <Box sx={{ mt: 3.25 }}>
          <Box
            component="button"
            type="button"
            onClick={onStay}
            autoFocus
            sx={{
              display: 'block',
              width: '100%',
              px: 3,
              py: 1.3,
              border: 'none',
              borderRadius: 999,
              background: `linear-gradient(135deg, ${colors.gold} 0%, ${GOLD} 100%)`,
              color: '#fff',
              font: 'inherit',
              fontSize: uiType.body,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: `0 8px 20px -10px ${GOLD}, inset 0 1px 0 rgba(255,255,255,0.28)`,
              transition: 'filter 0.15s, box-shadow 0.15s',
              '&:hover': { filter: 'brightness(1.05)' },
            }}
          >
            Stay Signed In
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component="button"
              type="button"
              onClick={onSignOut}
              sx={{
                mt: 1.5,
                px: 1,
                py: 0.5,
                border: 'none',
                bgcolor: 'transparent',
                color: colors.textMuted,
                font: 'inherit',
                fontSize: uiType.caption,
                fontWeight: 600,
                letterSpacing: '0.02em',
                cursor: 'pointer',
                transition: 'color 0.15s',
                '&:hover': { color: colors.textPrimary },
              }}
            >
              Sign Out Now
            </Box>
          </Box>
        </Box>

        <CornerBracket top left />
        <CornerBracket top left={false} />
        <CornerBracket top={false} left />
        <CornerBracket top={false} left={false} />
      </MotionBox>
    </Dialog>
  );
}
